// frontend/src/hooks/useReportFilters.js
//
// Filter state for report pages (location, product category, free-form extras).
//
// Persistence: per-tab via useTabPersistedState — same storage layer as
// every other heavy page (Forecast, Inventory, PR, PO, ItemSetup). Reports
// are NOT a special case: their filters survive F5 within a tab, are
// independent across tabs, and are wiped on browser close.
//
// ``productCategory`` is the full ProductCategory object ({id, display_label,
// ...}) or null. Pages send ``?product_category_id=<id>`` on the wire.

import { useCallback, useMemo } from 'react';
import { useTabPersistedState } from '../tabs/useTabPersistedState';

export const useReportFilters = (reportId, extraDefaults = {}) => {
  const [location, setLocation] = useTabPersistedState(
    `report.${reportId}.location`,
    'all',
  );
  const [productCategory, setProductCategory] = useTabPersistedState(
    `report.${reportId}.productCategory`,
    null,
  );
  const [storedExtras, setStoredExtras] = useTabPersistedState(
    `report.${reportId}.extras`,
    {},
  );

  // Merge caller-supplied defaults with stored values so caller can add
  // a new extra key without orphaning users whose stored state predates it.
  const extras = useMemo(
    () => ({ ...extraDefaults, ...storedExtras }),
    [extraDefaults, storedExtras],
  );

  const setExtra = useCallback(
    (key, value) => setStoredExtras((prev) => ({ ...(prev || {}), [key]: value })),
    [setStoredExtras],
  );

  return { location, setLocation, productCategory, setProductCategory, extras, setExtra };
};
